<template>
  <div
    class="bg-white rounded-xl "
    :class="{ 'p-8 shadow': type !== 'modal' }"
  >
    <h2 class="text-2xl font-bold text-gray-900 mb-6 text-center">Форма обратной связи</h2>

    <SharedRmField
      :field="formStorePath"
    >
      <form
        class="space-y-6"
        @submit.prevent="submitForm"
      >
        <!-- Имя -->
        <SharedRmField
          :ref="nameField"
          field="feedback/name"
          :error="nameError"
        >
          <label
            for="name"
            class="block text-sm font-medium text-gray-700 mb-2"
          >
            Имя
          </label>
          <input
            id="name"
            v-model="name"
            type="text"
            class="w-full px-4 py-3 border border-gray-300 rounded-[12px] focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
            placeholder="Введите ваше имя"
            @blur="handleNameBlur"
          />
        </SharedRmField>

        <!-- Телефон -->
        <SharedRmField
          :ref="phoneField"
          field="feedback/phone"
          :error="phoneError"
        >
          <label
            for="phone"
            class="block text-sm font-medium text-gray-700 mb-2"
          >
            Телефон <span class="text-red-500">*</span>
          </label>
          <SharedRmInputMask
            v-model="phone"
            :mask="'+7 (999) 999 9999'"
            placeholder="+7 999 999 9999"
            class="w-full px-4 py-3 border border-gray-300 rounded-[12px] focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
            @blur="handlePhoneBlur"
          />
        </SharedRmField>

        <!-- Email -->
        <SharedRmField
          :ref="emailField"
          field="feedback/email"
          :error="emailError"
        >
          <label
            for="email"
            class="block text-sm font-medium text-gray-700 mb-2"
          >
            Email <span class="text-red-500">*</span>
          </label>
          <input
            id="email"
            v-model="email"
            type="email"
            class="w-full px-4 py-3 border border-gray-300 rounded-[12px] focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
            placeholder="example@email.com"
            @blur="handleEmailBlur"
          />
        </SharedRmField>

        <!-- Сообщение -->
        <SharedRmField
          :ref="messageField"
          field="feedback/message"
          :error="messageError"
        >
          <label
            for="message"
            class="block text-sm font-medium text-gray-700 mb-2"
          >
            Сообщение
          </label>
          <textarea
            id="message"
            v-model="message"
            rows="4"
            class="w-full px-4 py-3 border border-gray-300 rounded-[12px] focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors resize-none"
            placeholder="Опишите ваш вопрос или предложение"
            @blur="handleMessageBlur"
          />
        </SharedRmField>

        <!-- Кнопка отправки -->
        <div class="text-center">
          <SharedRmBtn
            color="blue"
            type="submit"
            :disabled="isSubmitting || !canSubmit"
            class="w-full bg-blue-600 text-white py-3 px-6 rounded-[12px] font-semibold hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Отправить
          </SharedRmBtn>
        </div>
      </form>
    </SharedRmField>

    <div class="text-xs text-red-700 text-center mt-4">
      Нажимая на кнопку, вы даете согласие на обработку персональных данных и соглашаетесь c политикой конфиденциальности
    </div>
  </div>
</template>

<script setup lang="ts">
import { useField, useForm } from 'vee-validate'
import { computed, ref } from 'vue'

useValidationRules()

const props = withDefaults(defineProps<{
  type?: 'default' | 'modal'
}>(), {
  type: 'default',
})

const formStorePath = 'feedback'
const { $modal, $dayjs } = useNuxtApp()

const user = useUser().item
const cart = useCart().item

// Инициализируем форму с начальными значениями
const { resetForm } = useForm({
  initialValues: {
    name: user.value?.firstName || '',
    phone: user.value?.phone || '',
    email: user.value?.email || '',
    message: '',
  },
})


// Refs
const nameField = ref(null)
const phoneField = ref(null)
const emailField = ref(null)
const messageField = ref(null)

// Vee-validate для имени
const {
  value: name,
  errorMessage: nameError,
  handleBlur: handleNameBlur,
} = useField('name')

// Vee-validate для телефона
const {
  value: phone,
  errorMessage: phoneError,
  handleBlur: handlePhoneBlur,
} = useField('phone', (value) => {
  // Телефон обязателен, если не заполнен email
  if (!value && !email.value) {
    return 'Заполните телефон или email'
  }
  if (value) {
    // Проверяем формат +7 (999) 999 9999
    const phoneRegex = /^\+7 \(\d{3}\) \d{3} \d{4}$/
    if (!phoneRegex.test(value)) {
      return 'Введите корректный номер телефона'
    }
  }
  return true
})

// Vee-validate для email
const {
  value: email,
  errorMessage: emailError,
  handleBlur: handleEmailBlur,
} = useField('email', (value) => {
  // Email обязателен, если не заполнен телефон
  if (!value && !phone.value) {
    return 'Заполните телефон или email'
  }
  if (value) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(value)) {
      return 'Введите корректный email'
    }
  }
  return true
})

// Vee-validate для сообщения
const {
  value: message,
  errorMessage: messageError,
  handleBlur: handleMessageBlur,
} = useField('message')

// Состояние отправки
const isSubmitting = ref(false)

// Computed для проверки возможности отправки
const canSubmit = computed(() => {
  // Проверяем, что есть хотя бы один валидный контакт
  const hasValidPhone = phone.value && !phoneError.value
  const hasValidEmail = email.value && !emailError.value
  const hasAnyValue = phone.value || email.value

  return (hasValidPhone || hasValidEmail) && hasAnyValue
})


// Методы валидации
const isValid = () => {
  handleNameBlur()
  handlePhoneBlur()
  handleEmailBlur()
  handleMessageBlur()
  return !nameError.value && !phoneError.value && !emailError.value && !messageError.value
}

const submitFeedbackForm = useGlobal().submitFeedbackForm


const hasSelectedDate = computed(() => cart.value?.dateFrom && cart.value?.dateTo)

// Отправка формы
const submitForm = async () => {
  const { notify } = useNotification()

  // Проверяем валидацию всех полей
  if (!isValid()) {
    notify({
      type: 'error',
      text: 'Пожалуйста, заполните все поля корректно',
    })
    return
  }

  // Проверяем, что заполнен хотя бы один контакт
  if (!phone.value && !email.value) {
    notify({
      type: 'error',
      text: 'Пожалуйста, заполните телефон или email',
    })
    return
  }

  isSubmitting.value = true

  try {
    let messageResult = hasSelectedDate.value ? `Дата аренды: c ${$dayjs(cart.value?.dateFrom).format('D MMMM')} по ${$dayjs(cart.value?.dateTo).format('D MMMM')}. \n` : ''
    const messageValue = message.value?.trim() || ''
    if (messageValue) {
      messageResult += messageValue
    }

    const result = await submitFeedbackForm({
      name: name.value?.trim() || null,
      phone: phone.value?.trim() || null,
      email: email.value?.trim() || null,
      message: messageResult,
      formStorePath,
    })

    if (result) {

      // Очищаем форму и сбрасываем touch состояние
      clearForm()

      if (props.type === 'modal') {
        $modal.hide()
      }

      notify({
        type: 'success',
        text: 'Сообщение успешно отправлено! Мы свяжемся с вами в ближайшее время.',
        duration: 15000,
      })
    } else {
      throw new Error(result.errors[0].message || 'Ошибка отправки')
    }

  } catch (error) {
    notify({
      type: 'error',
      text: error.message || 'Ошибка отправки',
    })
  } finally {
    isSubmitting.value = false
  }
}

const clearForm = () => {
  resetForm()
}

// Expose methods
defineExpose({
  isValid,
  clearForm,
})
</script>
